import { EstadoOportunidad, Pestania } from '../../api/oportunidadesApi';
import type { DatosOrigen, ElementoCatalogo } from '../../api/oportunidadesApi';

// Reglas puras de la pantalla de Oportunidades (OPOR-I001). El backend las vuelve a
// comprobar; aquí solo sirven para señalar el campo antes de enviar y para rotular.

/** R-02 bis: el estado 2 se llama SIEMPRE «Descarte pendiente de confirmar» en la interfaz. */
export const ETIQUETA_ESTADO: Record<number, string> = {
  [EstadoOportunidad.Pendiente]: 'Pendiente',
  [EstadoOportunidad.DescartePendiente]: 'Descarte pendiente de confirmar',
  [EstadoOportunidad.Descartada]: 'Descartada',
  [EstadoOportunidad.Atendida]: 'Atendida',
};

export const etiquetaEstado = (id: number): string => ETIQUETA_ESTADO[id] ?? `Estado ${id}`;

/** R-03: filtro de estado por defecto. */
export const ESTADOS_POR_DEFECTO: number[] = [EstadoOportunidad.Pendiente, EstadoOportunidad.DescartePendiente];

/** R-08 (spec rev. 3): una operación se nombra «CCL - Alias», sin datos de la farmacia. */
export const etiquetaOperacion = (o: { idOperacion: number; ccl?: string; alias?: string }): string =>
  [o.ccl, o.alias].filter((x) => x && x.trim()).join(' - ') || `Operación ${o.idOperacion}`;

/** R-06 (spec rev. 3): el servicio solo aplica cuando el tipo de operación es Gestión. */
export const servicioAplica = (idTipoOperacion: number | undefined, idGestion: number | undefined): boolean =>
  idTipoOperacion !== undefined && idGestion !== undefined && idTipoOperacion === idGestion;

/** R-03 (spec rev. 3): al entrar, la casilla «Pendientes de asignar» va marcada. */
export const PENDIENTES_ASIGNAR_POR_DEFECTO = true;

export const NOMBRE_PESTANIA: Record<Pestania, string> = {
  [Pestania.Venta]: 'Venta',
  [Pestania.Compra]: 'Compra',
  [Pestania.Solicitud]: 'Solicitudes de información',
};

export const MINIMO_OBSERVACIONES = 5;
export const MAXIMO_OBSERVACIONES_VENTA = 500;

/** R-19: mínimo 5 caracteres sin contar los espacios del principio y del final. */
export const observacionesValidas = (texto?: string | null): boolean =>
  (texto ?? '').trim().length >= MINIMO_OBSERVACIONES;

/** R-12: al pasar a 2 o 3 el motivo es obligatorio. */
export const estadoRequiereMotivo = (idEstado?: number): boolean =>
  idEstado === EstadoOportunidad.DescartePendiente || idEstado === EstadoOportunidad.Descartada;

/** R-09: detalles del origen elegido (cascada). */
export const detallesDeOrigen = (detalles: ElementoCatalogo[], idOrigen?: number): ElementoCatalogo[] =>
  idOrigen === undefined ? [] : detalles.filter((d) => d.idPadre === idOrigen);

/**
 * R-09: faltas de origen/detalle/EPC. Con EPCs no hay detalle visible y hace falta la EPC entidad o
 * la persona; con cualquier otro origen, el detalle es obligatorio y los campos EPC no se usan.
 */
export const faltasOrigen = (d: Partial<DatosOrigen>, idOrigenEpc: number): string[] => {
  const faltas: string[] = [];
  if (!d.idIniciativa) faltas.push('La iniciativa es obligatoria.');
  if (!d.idOrigen) {
    faltas.push('El origen es obligatorio.');
  } else if (d.idOrigen === idOrigenEpc) {
    if (!d.idEpcEntidad && !d.idEpcPersona) faltas.push('Con origen EPCs indica la EPC entidad o la EPC persona.');
  } else if (!d.idDetalle) {
    faltas.push('El detalle del origen es obligatorio.');
  }
  return faltas;
};

/** R-09: al cambiar de origen se vacía el detalle; si deja de ser EPCs, también los campos EPC. */
export const limpiarAlCambiarOrigen = <T extends Partial<DatosOrigen>>(d: T, nuevoOrigen: number | undefined, idOrigenEpc: number): T => ({
  ...d,
  idOrigen: nuevoOrigen,
  idDetalle: undefined,
  ...(nuevoOrigen === idOrigenEpc ? {} : { idEpcEntidad: undefined, idEpcPersona: undefined }),
});

/** R-11: el aviso no se ofrece si la usuaria se asigna la oportunidad a sí misma. */
export const puedeNotificar = (idResponsable?: number, idYo?: number): boolean =>
  idResponsable !== undefined && idResponsable !== idYo;

/** R-02: observaciones recortadas en la columna (completas en el tooltip). */
export const recortar = (texto: string | undefined, maximo = 60): string => {
  const t = (texto ?? '').replace(/\s+/g, ' ').trim();
  return t.length <= maximo ? t : `${t.slice(0, maximo - 1).trimEnd()}…`;
};
